import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import prestacaoService, {
  type PrestacaoPc,
  type PrestacaoPlanoResumo,
} from '../services/prestacaoContaService';
import { UI_THEME } from '../ui/theme';
import PrestacaoContasAnexosPanel from './prestacaoContasRelatorios/PrestacaoContasAnexosPanel';
import { pcLabel } from './prestacaoContasRelatorios/shared';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import Button from '../components/ui/Button';
import PrestacaoPcAprovacaoPanel, { SituacaoPcBadge } from './prestacaoContasRelatorios/PrestacaoPcAprovacaoPanel';
import { toUpperText } from '../utils/formatters';

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
};

const PrestacaoContasPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const planoId = searchParams.get('planoId') || '';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [resumo, setResumo] = useState<PrestacaoPlanoResumo | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [prorrogacaoForm, setProrrogacaoForm] = useState<Record<string, { novoPrazo: string }>>({});
  const [ajusteForm, setAjusteForm] = useState<Record<string, string>>({});

  const role = String((user as any)?.role || (user as any)?.perfil || '').toUpperCase();
  const isAdmin = role === 'ADMIN';
  const isConvenios = role === 'CONVENIOS' || isAdmin;
  const podeEditarAnexos = ['ADMIN', 'CONVENIOS', 'OPERADOR'].includes(role) || Boolean((user as any)?.gestorParceria);
  const isSaas = UI_THEME === 'saas';

  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const subtleSurfaceClass = 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/70';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const inputClass = FIELD_CLASS;

  const pcs = resumo?.pcs || [];
  const hasPcs = pcs.length > 0;

  const titleResumo = useMemo(() => {
    if (!resumo?.prazoFinalPlano) return 'Prazo final ainda indefinido';
    return `Prazo final do plano: ${formatDate(resumo.prazoFinalPlano)}`;
  }, [resumo]);

  const fetchResumo = async () => {
    if (!planoId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await prestacaoService.getResumo(planoId);
      setResumo(data);
    } catch (err: any) {
      console.error('Erro ao carregar prestação de contas:', err);
      setError(err?.response?.data?.error || 'Falha ao carregar prestação de contas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planoId]);

  const handleProrrogar = async (pcId: string) => {
    const form = prorrogacaoForm[pcId];
    if (!form?.novoPrazo) {
      setError('Informe o novo prazo da PC.');
      return;
    }
    try {
      setSavingKey(`prorrogacao-${pcId}`);
      setError(null);
      await prestacaoService.prorrogarPrazo(pcId, planoId, form);
      await fetchResumo();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao prorrogar prazo');
    } finally {
      setSavingKey(null);
    }
  };

  const handleSolicitarAjuste = async (pcId: string) => {
    const mensagem = (ajusteForm[pcId] || '').trim();
    if (!mensagem) {
      setError('Descreva os ajustes necessários na prestação de contas.');
      return;
    }
    try {
      setSavingKey(`ajuste-${pcId}`);
      setError(null);
      await prestacaoService.solicitarAjuste(pcId, planoId, mensagem);
      setAjusteForm((prev) => ({ ...prev, [pcId]: '' }));
      await fetchResumo();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao solicitar ajuste');
    } finally {
      setSavingKey(null);
    }
  };

  const handleResolverAjuste = async (pcId: string) => {
    try {
      setSavingKey(`ajuste-resolver-${pcId}`);
      setError(null);
      await prestacaoService.resolverAjuste(pcId, planoId);
      await fetchResumo();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao marcar o ajuste como resolvido');
    } finally {
      setSavingKey(null);
    }
  };

  const renderPrazoBadge = (pc: PrestacaoPc) => {
    if (pc.aguardandoRepasse) {
      return <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">Aguardando repasse</span>;
    }
    if (pc.statusPrestacao === 'FECHADA' || pc.prazoExpirado) {
      return <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30">Fechado</span>;
    }
    if (typeof pc.diasRestantes === 'number' && pc.diasRestantes <= 7) {
      return <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30">Vence em {pc.diasRestantes} dia(s)</span>;
    }
    return <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">{pc.statusPrestacao}</span>;
  };

  const canProrrogar = (pc: PrestacaoPc) => isAdmin || pc.podeProrrogar;

  const totalRecebido = pcs.reduce((sum, pc) => sum + Number(pc.valorRecebido || 0), 0);
  const totalComprovantes = pcs.reduce((sum, pc) => sum + pc.comprovantes.length, 0);
  const pcsVencidas = pcs.filter((pc) => pc.prazoExpirado).length;
  const summaryCards = [
    { label: 'PCs', value: pcs.length, dot: 'bg-slate-400' },
    { label: 'Valor recebido', value: formatCurrency(totalRecebido), dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
    { label: 'Comprovantes', value: totalComprovantes, dot: 'bg-emerald-500' },
    { label: 'Prazos expirados', value: pcsVencidas, dot: pcsVencidas > 0 ? 'bg-red-500' : 'bg-slate-400' },
  ];

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Execução financeira' : 'Prestação de contas'}
            </p>
            <h1 className={`mt-2 text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
              Prestação de contas
            </h1>
            <p className={`mt-2 text-sm leading-6 ${bodyTextClass}`}>{titleResumo}</p>
          </div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {!planoId && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          Parâmetro `planoId` ausente na URL.
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {resumo?.alertaPrazoFinal && (
        <div className={`rounded-2xl border px-4 py-3 text-sm ${
          resumo.prazoFinalExpirado
            ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'
            : 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200'
        }`}>
          {resumo.alertaPrazoFinal}
        </div>
      )}

      {loading && !resumo ? (
        <div className={`rounded-3xl border p-6 text-sm ${surfaceClass} ${bodyTextClass}`}>Carregando...</div>
      ) : (
        <div className="space-y-4">
          {!hasPcs ? (
            <div className={`rounded-3xl border p-6 text-sm ${surfaceClass} ${bodyTextClass}`}>
              Nenhuma prestação de contas exigida até o momento. As PCs são definidas por Convênios na formalização do termo de fomento.
            </div>
          ) : (
            pcs.map((pc) => (
              <section key={pc.pcId} className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
                <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className={`text-lg font-semibold ${strongTextClass}`}>
                      {pcLabel(pc)} {pc.ehFinal && <span className={`ml-1 text-xs font-semibold ${accentTextClass}`}>(final)</span>}
                    </div>
                    <div className={`text-sm ${bodyTextClass}`}>
                      Protocolo: {pc.protocolo} · Valor recebido: {formatCurrency(pc.valorRecebido)}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <SituacaoPcBadge situacao={pc.situacao} />
                    {renderPrazoBadge(pc)}
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                      Prazo final: {formatDate(pc.prazoPrestacaoFinal)}
                    </span>
                  </div>
                </div>

                <PrestacaoPcAprovacaoPanel pc={pc} planoId={planoId} onChanged={fetchResumo} />

                {pc.ajustePendente && (
                  <div className="mx-5 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold">Ajustes solicitados na prestação de contas</p>
                        <p className="mt-1 whitespace-pre-wrap">{pc.ajusteSolicitadoMensagem}</p>
                        <p className="mt-1 text-rose-600 dark:text-rose-300">
                          Solicitado por {pc.ajusteSolicitadoPor || 'Convênios'}
                          {pc.ajusteSolicitadoEm ? ` em ${new Date(pc.ajusteSolicitadoEm).toLocaleDateString('pt-BR')}` : ''}
                        </p>
                      </div>
                      {(isAdmin || pc.podeResolverAjuste) && (
                        <Button variant="danger-outline" size="sm" className="shrink-0" onClick={() => handleResolverAjuste(pc.pcId)} disabled={savingKey === `ajuste-resolver-${pc.pcId}`}>
                          {savingKey === `ajuste-resolver-${pc.pcId}` ? 'Salvando...' : 'Marcar como resolvido'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {isConvenios && (
                  <details className="group border-t border-slate-200 text-xs dark:border-slate-800">
                    <summary className={`flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-3 font-semibold transition hover:bg-slate-50 dark:hover:bg-slate-800/60 ${strongTextClass}`}>
                      <span>Gerenciar prazo e ajustes</span>
                      <span className={`text-base font-normal transition group-open:rotate-45 ${labelTextClass}`}>+</span>
                    </summary>
                    <div className="grid gap-4 border-t border-slate-200 bg-slate-50/70 px-5 py-4 dark:border-slate-800 dark:bg-slate-800/40 lg:grid-cols-2">
                      <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <div>
                            <p className={`font-semibold ${strongTextClass}`}>Prorrogação do prazo</p>
                            <p className={labelTextClass}>Prazo original: {formatDate(pc.prazoPrestacaoOriginal)}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                          <div>
                            <label className={`mb-1 block font-medium ${labelTextClass}`}>Novo prazo</label>
                            <input
                              type="date"
                              value={prorrogacaoForm[pc.pcId]?.novoPrazo || pc.prazoPrestacaoFinal || ''}
                              onChange={(e) =>
                                setProrrogacaoForm((prev) => ({
                                  ...prev,
                                  [pc.pcId]: { novoPrazo: e.target.value },
                                }))
                              }
                              className={`${inputClass} max-w-[14rem]`}
                              disabled={!canProrrogar(pc)}
                            />
                          </div>
                          <Button variant="secondary" onClick={() => handleProrrogar(pc.pcId)} disabled={!canProrrogar(pc) || savingKey === `prorrogacao-${pc.pcId}`}>
                            {savingKey === `prorrogacao-${pc.pcId}` ? 'Prorrogando...' : 'Prorrogar'}
                          </Button>
                        </div>
                        {!canProrrogar(pc) && (
                          <p className={`mt-1.5 ${labelTextClass}`}>Disponível após o vencimento original.</p>
                        )}
                      </div>

                      <div className="border-t border-slate-200 pt-4 dark:border-slate-700 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0">
                        <label className={`mb-1 block font-semibold ${strongTextClass}`}>Solicitar correção ao operador</label>
                        <textarea
                          rows={2}
                          value={ajusteForm[pc.pcId] ?? ''}
                          onChange={(e) => setAjusteForm((prev) => ({ ...prev, [pc.pcId]: toUpperText(e.target.value) }))}
                          placeholder="Descreva objetivamente o que precisa ser corrigido."
                          className={inputClass}
                        />
                        <Button variant="secondary" className="mt-2" onClick={() => handleSolicitarAjuste(pc.pcId)} disabled={savingKey === `ajuste-${pc.pcId}` || !(ajusteForm[pc.pcId] || '').trim()}>
                          {savingKey === `ajuste-${pc.pcId}`
                            ? 'Enviando...'
                            : pc.ajustePendente
                              ? 'Reenviar solicitação'
                              : 'Notificar operador'}
                        </Button>
                      </div>
                    </div>
                  </details>
                )}
              </section>
            ))
          )}

          {hasPcs && (
            <section className={`overflow-hidden rounded-3xl border p-5 ${surfaceClass}`}>
              <PrestacaoContasAnexosPanel
                planoId={planoId}
                pcs={pcs}
                podeEditar={podeEditarAnexos}
                onAlterado={fetchResumo}
              />
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default PrestacaoContasPage;
